import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { resolveImageUrl } from "../lib/imageUrl";
import { 
  Diamond, 
  ShoppingBag, 
  Filter, 
  ChevronDown,
  Star,
  Heart,
  Plus,
  Menu,
  X
} from "lucide-react";
import CartDrawer from "../components/CartDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);

  const sessionId = localStorage.getItem("vk_session_id") || (() => {
    const id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("vk_session_id", id);
    return id;
  })();

  const categories = [
    { id: "all", name: "All" },
    { id: "watches", name: "Watches" },
    { id: "chains", name: "Chains" },
    { id: "pendants", name: "Pendants" },
    { id: "grillz", name: "Grillz" },
    { id: "rings", name: "Rings" },
    { id: "bracelets", name: "Bracelets" },
    { id: "earrings", name: "Earrings" },
    { id: "necklaces", name: "Necklaces" },
    { id: "anklets", name: "Anklets" }
  ];

  const fetchProducts = useCallback(async () => {
    try {
      await axios.post(`${API}/seed-products`);
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
      setFilteredProducts(res.data);
    } catch (err) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCartCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cart/${sessionId}`);
      const count = res.data.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
      setCartItemCount(count);
    } catch (err) {
      console.error("Error fetching cart:", err);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchProducts();
    fetchCartCount();
  }, [fetchProducts, fetchCartCount]);

  useEffect(() => {
    if (activeCategory === "all") {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter(p => p.category === activeCategory));
    }
  }, [activeCategory, products]);

  const addToCart = async (product, variant = null) => {
    try {
      await axios.post(`${API}/cart/add`, {
        session_id: sessionId,
        product_id: product.id,
        title: product.name,
        price: product.price,
        quantity: 1,
        selected_gem_variant: variant,
        product_image_url: product.image_url
      });
      toast.success("Added to cart!");
      fetchCartCount();
      setVariantDialogOpen(false); // Close variant dialog first
      setSelectedProduct(null);
      setSelectedVariant(null);
      setTimeout(() => setCartOpen(true), 100); // Open cart after dialog closes
    } catch (err) {
      toast.error("Failed to add to cart");
    }
  };

  const openVariantDialog = (product) => {
    setSelectedProduct(product);
    setSelectedVariant(product.gem_variants[0]);
    setVariantDialogOpen(true);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-[#080809]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="shop-navigation">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="shop-logo">
              <Diamond className="w-8 h-8 text-primary" />
              <span className="text-xl font-light tracking-tight text-[#F5F5F0]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                VK Gems & Jewels
              </span>
            </Link>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors">Home</Link>
              <Link to="/shop" className="text-sm text-primary">Shop</Link>
              <button 
                onClick={() => setCartOpen(true)}
                className="relative p-2 hover:bg-white/5 transition-colors"
                data-testid="cart-button"
              >
                <ShoppingBag className="w-6 h-6 text-[#F5F5F0]" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-[#080809] text-xs font-medium flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </button>
            </div>

            {/* Mobile Menu */}
            <div className="flex md:hidden items-center gap-4">
              <button 
                onClick={() => setCartOpen(true)}
                className="relative p-2"
                data-testid="cart-button-mobile"
              >
                <ShoppingBag className="w-6 h-6 text-[#F5F5F0]" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-[#080809] text-xs font-medium flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-6 h-6 text-[#F5F5F0]" /> : <Menu className="w-6 h-6 text-[#F5F5F0]" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-[#121214] border-t border-white/10 px-6 py-4">
            <div className="flex flex-col gap-4">
              <Link to="/" className="text-[#A1A1AA] hover:text-[#F5F5F0]" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <Link to="/shop" className="text-primary" onClick={() => setMobileMenuOpen(false)}>Shop</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 border-b border-white/5" data-testid="shop-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4">Luxury Collection</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#F5F5F0] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Exquisite Jewelry
          </h1>
          <p className="text-lg text-[#A1A1AA] max-w-2xl mx-auto font-light">
            Discover our curated collection of IGI & GIA certified luxury jewelry.
            Each piece is a testament to exceptional craftsmanship.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 border-b border-white/5" data-testid="category-filter">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center justify-center gap-4">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-2 text-sm transition-all ${
                  activeCategory === cat.id
                    ? "bg-primary text-[#080809]"
                    : "border border-white/20 text-[#A1A1AA] hover:border-white/40"
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product) => (
                <div 
                  key={product.id} 
                  className="glass-card group hover-lift transition-all duration-300"
                  data-testid={`product-card-${product.id}`}
                >
                  <Link to={`/product/${product.id}`} className="block" data-testid={`product-link-${product.id}`}>
                    <div className="relative aspect-square overflow-hidden">
                      <img 
                        src={resolveImageUrl(product.image_url)} 
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {product.featured && (
                        <span className="absolute top-4 left-4 px-3 py-1 bg-primary text-[#080809] text-xs font-medium uppercase tracking-wider">
                          Featured
                        </span>
                      )}
                    </div>
                  </Link>
                  
                  <div className="p-6">
                    <p className="text-xs text-primary uppercase tracking-wider mb-2">{product.gem_type || product.category}</p>
                    <Link to={`/product/${product.id}`}>
                      <h3 className="text-lg text-[#F5F5F0] mb-2 font-light hover:text-primary transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        {product.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-[#71717A] mb-4 line-clamp-2">{product.description}</p>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-[10px] text-[#71717A] uppercase tracking-wider">From</p>
                        <span className="text-xl text-[#F5F5F0] font-light">{formatPrice(product.lab_grown_price || product.price)}</span>
                        {product.natural_price && product.natural_price !== (product.lab_grown_price || product.price) && (
                          <p className="text-[10px] text-[#71717A] mt-0.5">Natural: {formatPrice(product.natural_price)}</p>
                        )}
                      </div>
                      <Link
                        to={`/product/${product.id}`}
                        className="p-3 bg-primary text-[#080809] hover:bg-[#F3E5AB] transition-colors"
                        data-testid={`view-product-${product.id}`}
                        aria-label="View product"
                      >
                        <Plus className="w-5 h-5" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <Diamond className="w-12 h-12 text-[#71717A] mx-auto mb-4" />
              <p className="text-[#71717A]">No products found in this category</p>
            </div>
          )}
        </div>
      </section>

      {/* Variant Selection Dialog */}
      <Dialog open={variantDialogOpen} onOpenChange={setVariantDialogOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-[#F5F5F0] max-w-md" data-testid="variant-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Select Variant</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Choose your preferred gemstone variant for {selectedProduct?.name}
            </DialogDescription>
          </DialogHeader>
          {selectedProduct && (
            <div className="space-y-4 mt-4">
              <div className="aspect-video overflow-hidden">
                <img src={resolveImageUrl(selectedProduct.image_url)} alt={selectedProduct.name} className="w-full h-full object-cover" />
              </div>
              <div className="space-y-2">
                <label className="text-xs text-[#71717A] uppercase tracking-wider">Gemstone Origin</label>
                <div className="flex flex-wrap gap-2">
                  {selectedProduct.gem_variants?.map((variant) => (
                    <button
                      key={variant}
                      onClick={() => setSelectedVariant(variant)}
                      className={`px-4 py-2 text-sm transition-all ${
                        selectedVariant === variant
                          ? "bg-primary text-[#080809]"
                          : "border border-white/20 text-[#A1A1AA] hover:border-white/40"
                      }`}
                      data-testid={`variant-${variant}`}
                    >
                      {variant}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={() => addToCart(selectedProduct, selectedVariant)}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="add-variant-to-cart"
              >
                <ShoppingBag className="w-5 h-5" />
                Add to Cart - {formatPrice(selectedProduct.price)}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cart Drawer */}
      <CartDrawer 
        open={cartOpen} 
        onClose={() => setCartOpen(false)} 
        sessionId={sessionId}
        onCartUpdate={fetchCartCount}
      />

      {/* Footer */}
      <footer className="py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Diamond className="w-6 h-6 text-primary" />
              <span className="text-lg font-light text-[#F5F5F0]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                VK Gems & Jewels
              </span>
            </div>
            <p className="text-sm text-[#71717A]">
              © 2025 VK Gems & Jewels. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
